/**
 * Home Page Component
 * 
 * @description Main landing page showcasing the complete portfolio
 * @features
 *  - SEO optimization with meta tags
 *  - Animated background with tentacle effects
 *  - Complete portfolio sections (About, Skills, Projects, Education, Experience, Contact, Hobbies)
 *  - Integrated ChatBot for user interaction
 *  - Responsive design for all device sizes
 */

import React from 'react';

// ================================
// COMPONENT IMPORTS
// ================================
import { SEO, ChatBot } from '../../../shared/components/indexComponents';
const LazyTentacleBackground = React.lazy(() => import('../../../shared/components/TentacleBackground/TentacleBackground'));
import { isTentacleEnabled, isTentacleForceMobileEnabled } from '../../../shared/services/featureFlags';
import { Header, Footer } from '../../layouts/index';
import {
  About,
  Skills,
  Projects,
  Experience,
  Education,
  Hobbies,
  Contact,
} from '../../components/index';

// ================================
// STYLES
// ================================
import * as styles from './homePage.css';

/**
 * HomePage Component
 * 
 * Renders the main portfolio page with all sections and interactive elements.
 * Implements proper semantic HTML structure for accessibility and SEO.
 * 
 * @returns {JSX.Element} Complete portfolio home page
 */
const HomePage: React.FC = (): JSX.Element => {
  const [showTentacle, setShowTentacle] = React.useState<boolean>(false);

  React.useEffect(() => {
    // Respecte le prefers-reduced-motion et charge à l'idle pour ne pas impacter FCP
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = typeof navigator !== 'undefined' && /Mobi|Android/i.test(navigator.userAgent);
    const saveData = typeof navigator !== 'undefined' && (navigator as any).connection && (navigator as any).connection.saveData;
    if (prefersReduced || isMobile || saveData) {
      setShowTentacle(false);
      return;
    }

    // ✅ CORRECTION BOUCLE INFINIE - Une seule vérification des feature flags
    let isMounted = true;
    const schedule = (cb: () => void) => {
      const ric = (window as any).requestIdleCallback as ((cb: Function, opts?: any) => any) | undefined;
      if (ric) return ric(cb, { timeout: 500 });
      return setTimeout(cb, 200);
    };
    
    const handle = schedule(async () => {
      if (!isMounted) return;
      try {
        const [enabled, forceMobile] = await Promise.all([
          isTentacleEnabled(),
          isTentacleForceMobileEnabled()
        ]);
        if (!isMounted) return;
        
        if (isMobile && !forceMobile) {
          setShowTentacle(false);
        } else {
          setShowTentacle(Boolean(enabled));
        }
      } catch (error) {
        console.warn('Erreur lors du chargement des feature flags tentacle:', error);
        if (isMounted) {
          setShowTentacle(false); // fallback sécurisé
        }
      }
    });
    
    return () => {
      isMounted = false;
      const cic = (window as any).cancelIdleCallback as ((id: any) => void) | undefined;
      if (cic && handle) cic(handle);
      else clearTimeout(handle);
    };
  }, []); // ✅ Dépendances vides - ne s'exécute qu'une fois

  // Réagir aux mises à jour live depuis le dashboard admin
  React.useEffect(() => {
    const onFlagsUpdated = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (typeof detail.tentacle === 'boolean') {
        // En live, appliquer la même règle mobile/forceMobile
        const isMobile = typeof navigator !== 'undefined' && /Mobi|Android/i.test(navigator.userAgent);
        const force = Boolean(detail['tentacle.forceMobile']);
        setShowTentacle(isMobile && !force ? false : detail.tentacle);
      }
    };
    window.addEventListener('featureFlagsUpdated', onFlagsUpdated as EventListener);
    return () => window.removeEventListener('featureFlagsUpdated', onFlagsUpdated as EventListener);
  }, []);

  return (
    <div className={styles.homePage}>
      {/* SEO Meta Tags */}
      <SEO />
      
      {/* Animated Background (feature-flagged) */}
      {showTentacle && (
        <React.Suspense fallback={null}>
          <LazyTentacleBackground />
        </React.Suspense>
      )}
      
      {/* Page Header */}
      <Header />
      
      {/* Main Content */}
      <main id="main-content" className={styles.main} role="main">
        <About />
        <Skills />
        <Projects />
        <Education />
        <Experience />
        <Hobbies />
        <Contact />
      </main>
      
      {/* Page Footer */}
      <Footer />
      
      {/* Interactive ChatBot */}
      <ChatBot />
    </div>
  );
};

export default HomePage;
